import { useState, useEffect } from 'react';
import './ImportPanel.css'; // reuse all the same form styles
import { supabase } from '../supabaseClient';

import ImageUpload from './ImageUpload';

const CATEGORIES = ['Main Course', 'Soup', 'Dessert', 'Baking', 'Breakfast', 'Starters', 'Side Dishes'];
const UNITS = ['', 'g', 'kg', 'ml', 'L', 'tbsp', 'tsp', 'count'];
const COMMON_TAGS = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'nut-free', 'low-carb', 'keto'];

// ─── Tag chip editor ─────────────────────────────────────────────────────────

function TagEditor({ tags, onChange }) {
  const [input, setInput] = useState('');

  function addTag(raw) {
    const tag = raw.trim().toLowerCase();
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput('');
  }

  function removeTag(tag) { onChange(tags.filter((t) => t !== tag)); }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
    else if (e.key === 'Backspace' && !input && tags.length) removeTag(tags[tags.length - 1]);
  }

  return (
    <div className="tag-editor">
      <div className="tag-editor__chips">
        {tags.map((tag) => (
          <span key={tag} className="tag-chip">
            {tag}
            <button type="button" className="tag-chip__remove" onClick={() => removeTag(tag)}>×</button>
          </span>
        ))}
        {COMMON_TAGS.filter((t) => !tags.includes(t)).map((t) => (
          <button key={t} type="button" className="tag-chip tag-chip--add" onClick={() => onChange([...tags, t])}>+ {t}</button>
        ))}
      </div>
      <input
        className="tag-editor__input" type="text" value={input}
        placeholder="Type a tag and press Enter…"
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addTag(input); }}
      />
    </div>
  );
}

// ─── Build initial form state from existing recipe data ──────────────────────

function buildForm(recipe, ingredients, steps, nutrition) {
  return {
    title: recipe.title || '',
    description: recipe.description || '',
    category: CATEGORIES.includes(recipe.category) ? recipe.category : 'Main Course',
    cuisine: recipe.cuisine || '',
    difficulty: ['easy', 'medium', 'hard'].includes(recipe.difficulty) ? recipe.difficulty : 'medium',
    prep_time_mins: recipe.prep_time_mins ?? '',
    cook_time_mins: recipe.cook_time_mins ?? '',
    default_servings: recipe.default_servings ?? 4,
    servings_unit: recipe.servings_unit || 'servings',
    ingredients: ingredients.map((ing) => ({ ...ing, _key: ing.id })),
    steps: steps.map((s) => ({ ...s, _key: s.id })),
    nutrition: nutrition
      ? {
          calories: nutrition.calories ?? '',
          protein_g: nutrition.protein_g ?? '',
          carbs_g: nutrition.carbs_g ?? '',
          fat_g: nutrition.fat_g ?? '',
          fiber_g: nutrition.fiber_g ?? '',
          sugar_g: nutrition.sugar_g ?? '',
          sodium_mg: nutrition.sodium_mg ?? '',
        }
      : { calories: '', protein_g: '', carbs_g: '', fat_g: '', fiber_g: '', sugar_g: '', sodium_mg: '' },
    dietary_tags: recipe.dietary_tags || [],
    equipment: recipe.equipment || [],
    notes: recipe.notes || '',
    storage_instructions: recipe.storage_instructions || '',
    image_url: recipe.image_url || '',
    source_url: recipe.source_url || '',
    is_traditional: recipe.is_traditional || false,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

function EditRecipePanel({ recipe, ingredients, steps, nutrition, onClose, onSaved, language }) {
  const isHu = language === 'hu';

  // EN mode: standard form state built from main recipe
  const [form, setForm] = useState(() => buildForm(recipe, ingredients, steps, nutrition));

  // HU mode: separate translatable field states loaded from recipe_translations
  const [huForm, setHuForm] = useState({
    title: '',
    description: '',
    notes: '',
    storage_instructions: '',
    dietary_tags: [],
    equipment: [],
    ingredients: [],
    steps: [],
  });
  const [huLoading, setHuLoading] = useState(isHu);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [calcNutriLoading, setCalcNutriLoading] = useState(false);
  const [calcNutriError, setCalcNutriError] = useState('');

  // Load HU translation on mount (HU mode only)
  useEffect(() => {
    if (!isHu) return;
    supabase
      .from('recipe_translations')
      .select('*')
      .eq('recipe_id', recipe.id)
      .eq('language_code', 'hu')
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          // ingredients and steps stored as jsonb arrays
          const huIngredients = (data.ingredients || []).map((ing, i) => ({
            _key: ingredients[i]?.id ?? `hu-${i}`,
            // non-translatable fields from the original
            amount: ingredients[i]?.amount ?? '',
            unit: ingredients[i]?.unit ?? '',
            sort_order: ingredients[i]?.sort_order ?? i + 1,
            ingredient_name: ing.ingredient_name || '',
            prep_notes: ing.prep_notes || '',
          }));
          const huSteps = (data.steps || []).map((s, i) => ({
            _key: steps[i]?.id ?? `hu-${i}`,
            step_number: i + 1,
            duration_mins: steps[i]?.duration_mins ?? '',
            instruction: s.instruction || '',
          }));
          setHuForm({
            title: data.title || '',
            description: data.description || '',
            notes: data.notes || '',
            storage_instructions: data.storage_instructions || '',
            dietary_tags: data.dietary_tags || [],
            equipment: data.equipment || [],
            ingredients: huIngredients,
            steps: huSteps,
          });
        } else {
          // No translation yet — pre-fill with English as starting point
          setHuForm({
            title: recipe.title || '',
            description: recipe.description || '',
            notes: recipe.notes || '',
            storage_instructions: recipe.storage_instructions || '',
            dietary_tags: recipe.dietary_tags || [],
            equipment: recipe.equipment || [],
            ingredients: ingredients.map((ing, i) => ({ ...ing, _key: ing.id })),
            steps: steps.map((s) => ({ ...s, _key: s.id })),
          });
        }
        setHuLoading(false);
      });
  }, [isHu, recipe, ingredients, steps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Form field helpers ────────────────────────────────────────────────────

  function setField(field, value) { setForm((prev) => ({ ...prev, [field]: value })); }
  function setHuField(field, value) { setHuForm((prev) => ({ ...prev, [field]: value })); }

  function setNutritionField(field, value) {
    setForm((prev) => ({ ...prev, nutrition: { ...prev.nutrition, [field]: value } }));
  }

  async function calculateNutrition() {
    if (!form.ingredients || form.ingredients.length === 0) {
      setCalcNutriError('Add some ingredients first.');
      return;
    }
    setCalcNutriLoading(true);
    setCalcNutriError('');
    try {
      const res = await fetch('/.netlify/functions/calculate-nutrition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ingredients: form.ingredients, servings: form.default_servings || 4 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const n = data.nutrition;
      setForm((prev) => ({
        ...prev,
        nutrition: {
          calories: Math.round(n.calories),
          protein_g: Math.round(n.protein_g),
          carbs_g: Math.round(n.carbs_g),
          fat_g: Math.round(n.fat_g),
          fiber_g: Math.round(n.fiber_g),
          sugar_g: Math.round(n.sugar_g),
          sodium_mg: Math.round(n.sodium_mg),
        },
      }));
    } catch (err) {
      setCalcNutriError('Could not calculate nutrition. Try again.');
    } finally {
      setCalcNutriLoading(false);
    }
  }

  // EN ingredient/step helpers
  function updateIngredient(key, field, value) {
    setForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => ing._key === key ? { ...ing, [field]: value } : ing),
    }));
  }
  function removeIngredient(key) {
    setForm((prev) => ({ ...prev, ingredients: prev.ingredients.filter((ing) => ing._key !== key) }));
  }
  function addIngredient() {
    const key = Date.now();
    setForm((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { _key: key, amount: '', unit: '', ingredient_name: '', prep_notes: '' }],
    }));
  }
  function updateStep(key, field, value) {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => s._key === key ? { ...s, [field]: value } : s),
    }));
  }
  function removeStep(key) {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.filter((s) => s._key !== key).map((s, i) => ({ ...s, step_number: i + 1 })),
    }));
  }
  function addStep() {
    const key = Date.now();
    setForm((prev) => ({
      ...prev,
      steps: [...prev.steps, { _key: key, step_number: prev.steps.length + 1, instruction: '', duration_mins: '' }],
    }));
  }

  // HU ingredient/step helpers
  function updateHuIngredient(key, field, value) {
    setHuForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => ing._key === key ? { ...ing, [field]: value } : ing),
    }));
  }
  function updateHuStep(key, value) {
    setHuForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => s._key === key ? { ...s, instruction: value } : s),
    }));
  }

  function addEquipment(item) {
    const t = item.trim();
    if (t && !form.equipment.includes(t)) setField('equipment', [...form.equipment, t]);
  }
  function removeEquipment(item) {
    setField('equipment', form.equipment.filter((e) => e !== item));
  }
  function addHuEquipment(item) {
    const t = item.trim();
    if (t && !huForm.equipment.includes(t)) setHuField('equipment', [...huForm.equipment, t]);
  }
  function removeHuEquipment(item) {
    setHuField('equipment', huForm.equipment.filter((e) => e !== item));
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaveError('');
    setSaving(true);

    try {
      if (isHu) {
        // HU mode: save translatable fields to recipe_translations only
        if (!huForm.title.trim()) { setSaveError('Title is required.'); setSaving(false); return; }

        const { error } = await supabase.from('recipe_translations').upsert({
          recipe_id: recipe.id,
          language_code: 'hu',
          title: huForm.title.trim(),
          description: huForm.description || null,
          notes: huForm.notes || null,
          storage_instructions: huForm.storage_instructions || null,
          dietary_tags: huForm.dietary_tags.length ? huForm.dietary_tags : null,
          equipment: huForm.equipment.length ? huForm.equipment : null,
          ingredients: huForm.ingredients.map(ing => ({
            ingredient_name: ing.ingredient_name,
            prep_notes: ing.prep_notes || null,
          })),
          steps: huForm.steps.map(s => ({ instruction: s.instruction })),
        }, { onConflict: 'recipe_id,language_code' });

        if (error) throw new Error(error.message);

      } else {
        // EN mode: update main recipes table
        if (!form.title.trim()) { setSaveError('Title is required.'); setSaving(false); return; }

        const { error: recipeErr } = await supabase
          .from('recipes')
          .update({
            title: form.title.trim(),
            description: form.description || null,
            category: form.category,
            cuisine: form.cuisine || null,
            difficulty: form.difficulty,
            prep_time_mins: form.prep_time_mins !== '' ? Number(form.prep_time_mins) : null,
            cook_time_mins: form.cook_time_mins !== '' ? Number(form.cook_time_mins) : null,
            default_servings: form.default_servings !== '' ? Number(form.default_servings) : 4,
            servings_unit: form.servings_unit || 'servings',
            dietary_tags: form.dietary_tags.length ? form.dietary_tags : null,
            equipment: form.equipment.length ? form.equipment : null,
            notes: form.notes || null,
            storage_instructions: form.storage_instructions || null,
            image_url: form.image_url || null,
            source_url: form.source_url || null,
            is_traditional: form.is_traditional || false,
            is_modified: true,
          })
          .eq('id', recipe.id);

        if (recipeErr) throw new Error(recipeErr.message);

        // Replace ingredients
        await supabase.from('recipe_ingredients').delete().eq('recipe_id', recipe.id);
        const newIngredients = form.ingredients
          .filter((ing) => ing.ingredient_name.trim())
          .map((ing, i) => ({
            recipe_id: recipe.id,
            sort_order: i + 1,
            amount: ing.amount !== '' && ing.amount !== null ? Number(ing.amount) : null,
            unit: ing.unit || null,
            ingredient_name: ing.ingredient_name.trim(),
            prep_notes: ing.prep_notes || null,
          }));
        if (newIngredients.length) {
          const { error: ingErr } = await supabase.from('recipe_ingredients').insert(newIngredients);
          if (ingErr) throw new Error(ingErr.message);
        }

        // Replace steps
        await supabase.from('recipe_steps').delete().eq('recipe_id', recipe.id);
        const newSteps = form.steps
          .filter((s) => s.instruction.trim())
          .map((s, i) => ({
            recipe_id: recipe.id,
            step_number: i + 1,
            instruction: s.instruction.trim(),
            duration_mins: s.duration_mins !== '' && s.duration_mins !== null ? Number(s.duration_mins) : null,
          }));
        if (newSteps.length) {
          const { error: stepsErr } = await supabase.from('recipe_steps').insert(newSteps);
          if (stepsErr) throw new Error(stepsErr.message);
        }

        // Nutrition upsert/delete
        const n = form.nutrition;
        const hasNutrition = Object.values(n).some((v) => v !== '' && v !== null);
        if (hasNutrition) {
          const { error: nutErr } = await supabase.from('recipe_nutrition').upsert({
            recipe_id: recipe.id,
            calories: n.calories !== '' ? Number(n.calories) : null,
            protein_g: n.protein_g !== '' ? Number(n.protein_g) : null,
            carbs_g: n.carbs_g !== '' ? Number(n.carbs_g) : null,
            fat_g: n.fat_g !== '' ? Number(n.fat_g) : null,
            fiber_g: n.fiber_g !== '' ? Number(n.fiber_g) : null,
            sugar_g: n.sugar_g !== '' ? Number(n.sugar_g) : null,
            sodium_mg: n.sodium_mg !== '' ? Number(n.sodium_mg) : null,
          });
          if (nutErr) throw new Error(nutErr.message);
        } else {
          await supabase.from('recipe_nutrition').delete().eq('recipe_id', recipe.id);
        }

        // Fire-and-forget: re-translate to Hungarian after EN edit
        // Pass originals so we can preserve manual HU edits on unchanged fields
        translateAndStore(recipe.id, form, recipe.tips || [], recipe, ingredients, steps);
      }

      onSaved();
    } catch (err) {
      setSaveError(`Save failed: ${err.message}`);
      setSaving(false);
    }
  }

  async function translateAndStore(recipeId, form, tips, originalRecipe, originalIngredients, originalSteps) {
    try {
      // Fetch existing HU translation so we can preserve manually-corrected fields
      const { data: existingHu } = await supabase
        .from('recipe_translations')
        .select('*')
        .eq('recipe_id', recipeId)
        .eq('language_code', 'hu')
        .maybeSingle();

      const res = await fetch('/.netlify/functions/translate-recipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          dietary_tags: form.dietary_tags || [],
          equipment: form.equipment || [],
          storage_instructions: form.storage_instructions || '',
          notes: form.notes || '',
          ingredients: form.ingredients.map(ing => ({
            ingredient_name: ing.ingredient_name,
            prep_notes: ing.prep_notes,
          })),
          steps: form.steps.map(s => ({ instruction: s.instruction })),
          tips,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) return;

      // For each translatable field: if the EN source didn't change AND a manual HU value
      // exists, keep the manual HU value. Only use the fresh translation for fields that
      // actually changed in English.
      const orig = originalRecipe || {};

      function enChanged(field) {
        return (form[field] || '') !== (orig[field] || '');
      }
      function arrChanged(formArr, origArr) {
        return JSON.stringify(formArr) !== JSON.stringify(origArr);
      }

      const ingredientsChanged = arrChanged(
        form.ingredients.map(i => ({ n: i.ingredient_name, p: i.prep_notes || '' })),
        (originalIngredients || []).map(i => ({ n: i.ingredient_name, p: i.prep_notes || '' }))
      );
      const stepsChanged = arrChanged(
        form.steps.map(s => s.instruction),
        (originalSteps || []).map(s => s.instruction)
      );
      const tagsChanged = arrChanged(form.dietary_tags || [], orig.dietary_tags || []);
      const equipChanged = arrChanged(form.equipment || [], orig.equipment || []);

      // pick: use fresh translation if EN changed or no manual HU exists, else keep existing HU
      function pick(field, freshValue, existingValue) {
        if (enChanged(field) || existingValue === undefined || existingValue === null) return freshValue;
        return existingValue;
      }

      await supabase.from('recipe_translations').upsert({
        recipe_id: recipeId,
        language_code: 'hu',
        title:                 pick('title',                data.title,                existingHu?.title),
        description:           pick('description',          data.description,          existingHu?.description),
        storage_instructions:  pick('storage_instructions', data.storage_instructions || null, existingHu?.storage_instructions),
        notes:                 pick('notes',                data.notes || null,        existingHu?.notes),
        dietary_tags:          (tagsChanged || !existingHu?.dietary_tags) ? (data.dietary_tags?.length ? data.dietary_tags : null) : existingHu.dietary_tags,
        equipment:             (equipChanged || !existingHu?.equipment)   ? (data.equipment?.length ? data.equipment : null)       : existingHu.equipment,
        ingredients:           (ingredientsChanged || !existingHu?.ingredients) ? data.ingredients : existingHu.ingredients,
        steps:                 (stepsChanged || !existingHu?.steps)       ? data.steps            : existingHu.steps,
        tips: data.tips, // always update tips (auto-generated, not manually edited)
      }, { onConflict: 'recipe_id,language_code' });
    } catch (_) {
      // silently fail
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (isHu && huLoading) {
    return (
      <div className="import-overlay">
        <div className="import-panel">
          <div className="import-panel__header">
            <h2 className="import-panel__title">Recept szerkesztése</h2>
            <button className="import-panel__close" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="import-panel__body" style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            Betöltés…
          </div>
        </div>
      </div>
    );
  }

  // ── HU edit mode ──────────────────────────────────────────────────────────
  if (isHu) {
    return (
      <div className="import-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="import-panel">
          <div className="import-panel__header">
            <h2 className="import-panel__title">Recept szerkesztése</h2>
            <button className="import-panel__close" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="import-panel__body">
            <div className="preview-form">

              <p style={{ fontSize: '0.85rem', color: '#9a7a6a', marginBottom: 16, padding: '8px 12px', background: '#fdf6f0', borderRadius: 6, border: '1px solid #e8d8cc' }}>
                🇭🇺 Magyar szerkesztési mód — a fordítható mezők szerkeszthetők. A nem fordítható mezők (kategória, idők, adagok, kép stb.) az angol szerkesztőben módosíthatók.
              </p>

              {/* Basic info — translatable */}
              <section className="form-section">
                <h3 className="form-section__title">Alapadatok</h3>
                <div className="form-group">
                  <label className="form-label">Cím *</label>
                  <input className="form-input" type="text" value={huForm.title} onChange={(e) => setHuField('title', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Leírás</label>
                  <textarea className="form-textarea" value={huForm.description} onChange={(e) => setHuField('description', e.target.value)} rows={3} />
                </div>
              </section>

              {/* Ingredients — translatable fields only */}
              <section className="form-section">
                <h3 className="form-section__title">Hozzávalók</h3>
                <p style={{ fontSize: '0.8rem', color: '#9a7a6a', marginBottom: 8 }}>Csak a neve és az előkészítési megjegyzés szerkeszthető. A mennyiségek és mértékegységek automatikusan megmaradnak.</p>
                <div className="ingredients-list">
                  <div className="ingredients-list__header">
                    <span>Mennyiség</span><span>Egység</span><span>Hozzávaló neve</span><span>Megjegyzés</span>
                  </div>
                  {huForm.ingredients.map((ing) => (
                    <div key={ing._key} className="ingredient-row">
                      <input className="form-input" type="text" value={ing.amount ?? ''} disabled style={{ background: '#f5f5f5', color: '#999' }} />
                      <input className="form-input" type="text" value={ing.unit ?? ''} disabled style={{ background: '#f5f5f5', color: '#999' }} />
                      <input className="form-input" type="text" value={ing.ingredient_name} onChange={(e) => updateHuIngredient(ing._key, 'ingredient_name', e.target.value)} placeholder="hozzávaló neve" />
                      <input className="form-input" type="text" value={ing.prep_notes ?? ''} onChange={(e) => updateHuIngredient(ing._key, 'prep_notes', e.target.value)} placeholder="pl. finomra vágva" />
                    </div>
                  ))}
                </div>
              </section>

              {/* Steps — translatable */}
              <section className="form-section">
                <h3 className="form-section__title">Elkészítési lépések</h3>
                <div className="steps-list">
                  {huForm.steps.map((s, idx) => (
                    <div key={s._key} className="step-row">
                      <div className="step-row__number">{idx + 1}</div>
                      <textarea className="form-textarea step-row__instruction" value={s.instruction} onChange={(e) => updateHuStep(s._key, e.target.value)} rows={2} placeholder="Lépés leírása…" />
                      <div className="step-row__duration">
                        <input className="form-input" type="text" value={s.duration_mins ?? ''} disabled style={{ background: '#f5f5f5', color: '#999' }} />
                        <span className="step-row__duration-label">perc</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Tags & extras — translatable */}
              <section className="form-section">
                <h3 className="form-section__title">Címkék és egyéb</h3>
                <div className="form-group">
                  <label className="form-label">Diétás címkék</label>
                  <TagEditor tags={huForm.dietary_tags} onChange={(tags) => setHuField('dietary_tags', tags)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Eszközök</label>
                  <div className="tag-editor">
                    <div className="tag-editor__chips">
                      {huForm.equipment.map((item) => (
                        <span key={item} className="tag-chip">
                          {item}
                          <button type="button" className="tag-chip__remove" onClick={() => removeHuEquipment(item)}>×</button>
                        </span>
                      ))}
                    </div>
                    <input
                      className="tag-editor__input" type="text"
                      placeholder="Adj hozzá eszközt és nyomj Entert…"
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                          e.preventDefault(); addHuEquipment(e.target.value); e.target.value = '';
                        }
                      }}
                      onBlur={(e) => { if (e.target.value.trim()) { addHuEquipment(e.target.value); e.target.value = ''; } }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Megjegyzések</label>
                  <textarea className="form-textarea" value={huForm.notes} onChange={(e) => setHuField('notes', e.target.value)} rows={2} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tárolási útmutató</label>
                  <textarea className="form-textarea" value={huForm.storage_instructions} onChange={(e) => setHuField('storage_instructions', e.target.value)} rows={2} />
                </div>
              </section>

              {saveError && <p className="import-error">{saveError}</p>}
              <div className="preview-form__actions">
                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>Mégsem</button>
                <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Mentés…' : 'Változtatások mentése'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── EN edit mode (default) ────────────────────────────────────────────────
  return (
    <div className="import-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="import-panel">

        <div className="import-panel__header">
          <h2 className="import-panel__title">Edit Recipe</h2>
          <button className="import-panel__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="import-panel__body">
          <div className="preview-form">

            {/* Basic info */}
            <section className="form-section">
              <h3 className="form-section__title">Basic info</h3>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" type="text" value={form.title} onChange={(e) => setField('title', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description} onChange={(e) => setField('description', e.target.value)} rows={3} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={(e) => setField('category', e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Cuisine</label>
                  <input className="form-input" type="text" value={form.cuisine} onChange={(e) => setField('cuisine', e.target.value)} placeholder="e.g. British, Italian" />
                </div>
                <div className="form-group">
                  <label className="form-label">Difficulty</label>
                  <select className="form-select" value={form.difficulty} onChange={(e) => setField('difficulty', e.target.value)}>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Prep time (mins)</label>
                  <input className="form-input" type="number" min="0" value={form.prep_time_mins} onChange={(e) => setField('prep_time_mins', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Cook time (mins)</label>
                  <input className="form-input" type="number" min="0" value={form.cook_time_mins} onChange={(e) => setField('cook_time_mins', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Servings</label>
                  <input className="form-input" type="number" min="1" value={form.default_servings} onChange={(e) => setField('default_servings', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Servings unit</label>
                  <input className="form-input" type="text" value={form.servings_unit} onChange={(e) => setField('servings_unit', e.target.value)} placeholder="servings" />
                </div>
              </div>
            </section>

            {/* Ingredients */}
            <section className="form-section">
              <h3 className="form-section__title">Ingredients</h3>
              <div className="ingredients-list">
                <div className="ingredients-list__header">
                  <span>Amount</span><span>Unit</span><span>Ingredient</span><span>Prep notes</span><span />
                </div>
                {form.ingredients.map((ing) => (
                  <div key={ing._key} className="ingredient-row">
                    <input className="form-input" type="number" min="0" step="any" value={ing.amount ?? ''} onChange={(e) => updateIngredient(ing._key, 'amount', e.target.value)} placeholder="—" />
                    <select className="form-select" value={ing.unit ?? ''} onChange={(e) => updateIngredient(ing._key, 'unit', e.target.value)}>
                      {UNITS.map((u) => <option key={u} value={u}>{u || '—'}</option>)}
                    </select>
                    <input className="form-input" type="text" value={ing.ingredient_name} onChange={(e) => updateIngredient(ing._key, 'ingredient_name', e.target.value)} placeholder="ingredient" />
                    <input className="form-input" type="text" value={ing.prep_notes ?? ''} onChange={(e) => updateIngredient(ing._key, 'prep_notes', e.target.value)} placeholder="e.g. finely chopped" />
                    <button type="button" className="row-delete-btn" onClick={() => removeIngredient(ing._key)}>×</button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn--add-row" onClick={addIngredient}>+ Add ingredient</button>
            </section>

            {/* Steps */}
            <section className="form-section">
              <h3 className="form-section__title">Steps</h3>
              <div className="steps-list">
                {form.steps.map((s, idx) => (
                  <div key={s._key} className="step-row">
                    <div className="step-row__number">{idx + 1}</div>
                    <textarea className="form-textarea step-row__instruction" value={s.instruction} onChange={(e) => updateStep(s._key, 'instruction', e.target.value)} rows={2} placeholder="Step instruction…" />
                    <div className="step-row__duration">
                      <input className="form-input" type="number" min="0" value={s.duration_mins ?? ''} onChange={(e) => updateStep(s._key, 'duration_mins', e.target.value)} placeholder="mins" />
                      <span className="step-row__duration-label">mins</span>
                    </div>
                    <button type="button" className="row-delete-btn" onClick={() => removeStep(s._key)}>×</button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn--add-row" onClick={addStep}>+ Add step</button>
            </section>

            {/* Tags & extras */}
            <section className="form-section">
              <h3 className="form-section__title">Tags &amp; extras</h3>
              <div className="form-group">
                <label className="form-label">Dietary tags</label>
                <TagEditor tags={form.dietary_tags} onChange={(tags) => setField('dietary_tags', tags)} />
              </div>
              <div className="form-group">
                <label className="traditional-toggle">
                  <input
                    type="checkbox"
                    checked={!!form.is_traditional}
                    onChange={e => setField('is_traditional', e.target.checked)}
                  />
                  <span className="traditional-toggle__icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C10.5 4.5 7 5 5 4c1 3 0 6-2 7.5 2 .5 3.5 2 4 4 1-1.5 2.5-2.5 4-2.5s3 1 4 2.5c.5-2 2-3.5 4-4C17 10 16 7 17 4c-2 1-5.5.5-5-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    </svg>
                  </span>
                  <span className="traditional-toggle__label">Traditional recipe</span>
                  <span className="traditional-toggle__hint">Mark this as a traditional or authentic recipe</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Equipment</label>
                <div className="tag-editor">
                  <div className="tag-editor__chips">
                    {form.equipment.map((item) => (
                      <span key={item} className="tag-chip">
                        {item}
                        <button type="button" className="tag-chip__remove" onClick={() => removeEquipment(item)}>×</button>
                      </span>
                    ))}
                  </div>
                  <input
                    className="tag-editor__input" type="text"
                    placeholder="Add equipment and press Enter…"
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                        e.preventDefault(); addEquipment(e.target.value); e.target.value = '';
                      }
                    }}
                    onBlur={(e) => { if (e.target.value.trim()) { addEquipment(e.target.value); e.target.value = ''; } }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} placeholder="Any extra tips or notes…" />
              </div>
              <div className="form-group">
                <label className="form-label">Storage instructions</label>
                <textarea className="form-textarea" value={form.storage_instructions} onChange={(e) => setField('storage_instructions', e.target.value)} rows={2} placeholder="e.g. Keeps in the fridge for 3 days…" />
              </div>
            </section>

            {/* Nutrition */}
            <section className="form-section">
              <h3 className="form-section__title">Nutrition <span className="form-section__sub">(per serving — optional)</span></h3>
              <div className="form-row form-row--wrap">
                {[
                  { field: 'calories', label: 'Calories (kcal)' },
                  { field: 'protein_g', label: 'Protein (g)' },
                  { field: 'carbs_g', label: 'Carbs (g)' },
                  { field: 'fat_g', label: 'Fat (g)' },
                  { field: 'fiber_g', label: 'Fibre (g)' },
                  { field: 'sugar_g', label: 'Sugar (g)' },
                  { field: 'sodium_mg', label: 'Sodium (mg)' },
                ].map(({ field, label }) => (
                  <div key={field} className="form-group form-group--narrow">
                    <label className="form-label">{label}</label>
                    <input className="form-input" type="number" min="0" step="any" value={form.nutrition[field] ?? ''} onChange={(e) => setNutritionField(field, e.target.value)} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" className="tips-generate-btn" onClick={calculateNutrition} disabled={calcNutriLoading}>
                  {calcNutriLoading ? 'Calculating…' : 'Calculate nutrition'}
                </button>
                {calcNutriError && <span style={{ fontSize: '0.85rem', color: '#c0392b' }}>{calcNutriError}</span>}
              </div>
            </section>

            {/* Image & source */}
            <section className="form-section">
              <h3 className="form-section__title">Image &amp; source</h3>
              <div className="form-group">
                <label className="form-label">Image</label>
                {form.image_url && (
                  <img src={form.image_url} alt="Preview" className="image-preview" onError={(e) => (e.target.style.display = 'none')} />
                )}
                <ImageUpload
                  folder={`recipes/${recipe.id}`}
                  onUploaded={(url) => setField('image_url', url)}
                />
                <p className="form-label" style={{ marginTop: 10 }}>Or paste a URL</p>
                <input className="form-input" type="url" value={form.image_url} onChange={(e) => setField('image_url', e.target.value)} placeholder="https://…" />
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <input className="form-input" type="text" value={form.source_url} onChange={(e) => setField('source_url', e.target.value)} placeholder="e.g. BBC Good Food, Mum's cookbook, Jamie's Italy p.45" />
              </div>
            </section>

            {saveError && <p className="import-error">{saveError}</p>}
            <div className="preview-form__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default EditRecipePanel;
